import React, { useState, useEffect, useRef, useCallback } from "react";
import env from "@beam-australia/react-env";

import { BlockUI } from "primereact/blockui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";

import { useTranslation } from "react-i18next";

import { useDebounce } from "../../../../hooks/useDebounce";
import useCRUDService from "../../../../hooks/useCRUDService";
import useApi from "../../../../hooks/useApi";
import ActiveInactive from "../../../../components/activeInactive/activeInactive";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";

import helperFormats from "../../../../utils/helperFormats";

const Credits = () => {
  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("credits");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(100);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");

  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "id",
          type: "number",
        },
        {
          name: "openedDate",
          type: "date",
        },

        {
          name: "application.amount",
          type: "number",
        },
        {
          name: "application.paymentsAmount",
          type: "number",
        },
        {
          name: "application.paymentsNumber",
          type: "number",
        },
        {
          name: "application.annualRate",
          type: "number",
        },
        {
          name: "application.annualDefaultRate",
          type: "number",
        },
        {
          name: "application.administrationFeeRate",
          type: "number",
        },
      ]);

      const response = await CRUDService.getEntities(
        columns,
        first,
        rows,
        sortField ?? "id",
        sortOrder >= 0 ? "ASC" : "DESC",
        debouncedSearchTerm
      );

      setTotalRecords(response.data.totalRows);
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm, CRUDService]);

  useEffect(() => {
    fetchData();
  }, [first, rows, sortField, sortOrder, debouncedSearchTerm]);

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={(e) => setGlobalFilter(e.target.value)}
          placeholder={`${t("DataTable.Search")}`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary buttonExport"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  return (
    <div className={s.get("dataTableContainer")}>
      <Toast ref={toast} />
      <DataTable
        onPage={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        ref={dt}
        value={entities}
        lazy
        paginator
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        columnResizeMode="fit"
        showGridlines
        dataKey="id"
        rowsPerPageOptions={[10, 25, 100, 1000]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={`${t(
          "DataTable.currentPageReportTemplate"
        )} ${t("entities.credits.plural")}`}
        globalFilter={globalFilter}
        header={header}
        className="flex flex-column w-full pl-2 pr-2"
      >
        <Column field="id" header={t("entities.credits.id")} sortable></Column>
        <Column
          field="openedDate"
          header={t("entities.credits.openedDate")}
          body={(rowData) =>
            helperFormats.isDate(rowData.openedDate)
              ? helperFormats.formatDate(new Date(rowData.openedDate))
              : "Pendiente"
          }
          sortable
        ></Column>

        <Column
          field="application.amount"
          header={t("entities.credits.application.amount")}
          sortable
        ></Column>
        <Column
          field="application.paymentsAmount"
          header={t("entities.credits.application.paymentsAmount")}
          sortable
        ></Column>
        <Column
          field="application.paymentsNumber"
          header={t("entities.credits.application.paymentsNumber")}
          sortable
        ></Column>
        <Column
          field="application.annualRate"
          header={t("entities.credits.application.annualRate")}
          sortable
        ></Column>
        <Column
          field="application.annualDefaultRate"
          header={t("entities.credits.application.annualDefaultRate")}
          sortable
        ></Column>
        <Column
          field="application.administrationFeeRate"
          header={t("entities.credits.application.administrationFeeRate")}
          sortable
        ></Column>
      </DataTable>
    </div>
  );
};

export default Credits;
